
import { useState, useEffect, memo, useMemo, useCallback } from "react";
import { Lightbulb, ArrowRight, ArrowLeft } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";

interface Tip {
  id: string;
  text: string;
  category: string;
  color: string;
  display_order: number;
}

const DailyTip = memo(() => {
  const [currentTip, setCurrentTip] = useState(0);
  const [tips, setTips] = useState<Tip[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTips = async () => {
      try {
        const { data } = await supabase
          .from('daily_tips')
          .select('id,text,category,color,display_order')
          .eq('is_active', true)
          .order('display_order', { ascending: true });
        
        if (data) {
          setTips(data);
        }
      } catch (error) {
        console.error('Error fetching tips:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTips();
  }, []);

  const tipsList = useMemo(() => tips || [], [tips]);

  const nextTip = useCallback(() => {
    setCurrentTip((prev) => (prev + 1) % tipsList.length);
  }, [tipsList.length]);

  const prevTip = useCallback(() => {
    setCurrentTip((prev) => (prev === 0 ? tipsList.length - 1 : prev - 1));
  }, [tipsList.length]);

  useEffect(() => {
    if (tipsList.length > 1) {
      const interval = setInterval(nextTip, 10000);
      return () => clearInterval(interval);
    }
  }, [tipsList.length, nextTip]);

  // Fallback tips if database fetch fails
  const fallbackTips = useMemo(() => [
    {
      id: 'fallback-1',
      text: "Turn daily routines into learning fun! Count steps, toys, or bites at snack time!",
      category: "Math",
      color: "bg-blue-100",
      display_order: 1
    }
  ], []);

  const finalTips = tipsList.length > 0 ? tipsList : fallbackTips;

  if (loading) {
    return (
      <section className="py-10 bg-gradient-to-r from-white to-tinyminds-lightPurple">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-fredoka text-center text-tinyminds-purple mb-6 flex items-center justify-center">
            <Lightbulb className="mr-2 text-tinyminds-yellow" fill="currentColor" />
            Today's Learning Tip
          </h2>
          <Card className="max-w-3xl mx-auto shadow-lg border-t-4 border-tinyminds-purple">
            <CardContent className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  if (finalTips.length === 0) {
    return null;
  }

  return (
    <section className="py-10 bg-gradient-to-r from-white to-tinyminds-lightPurple">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl font-fredoka text-center text-tinyminds-purple mb-6 flex items-center justify-center">
          <Lightbulb className="mr-2 text-tinyminds-yellow" fill="currentColor" />
          Today's Learning Tip
        </h2>
        
        <Card className={`max-w-3xl mx-auto shadow-lg border-t-4 border-tinyminds-purple transition-all duration-500 ${finalTips[currentTip].color}`}>
          <CardContent className="p-6">
            <div className="flex items-start mb-4">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-white">
                {finalTips[currentTip].category}
              </span>
            </div>
            <p className="text-lg text-gray-700 italic">"{finalTips[currentTip].text}"</p>
            
            {finalTips.length > 1 && (
              <div className="flex justify-between items-center mt-6">
                <button 
                  onClick={prevTip}
                  className="p-2 rounded-full bg-white text-tinyminds-purple hover:bg-tinyminds-purple hover:text-white transition-colors"
                  aria-label="Previous tip"
                >
                  <ArrowLeft size={16} />
                </button>
                
                <div className="flex space-x-1">
                  {finalTips.map((_, index) => (
                    <span 
                      key={index}
                      className={`block w-2 h-2 rounded-full ${currentTip === index ? 'bg-tinyminds-purple' : 'bg-gray-300'}`}
                    />
                  ))}
                </div>
                
                <button 
                  onClick={nextTip}
                  className="p-2 rounded-full bg-white text-tinyminds-purple hover:bg-tinyminds-purple hover:text-white transition-colors"
                  aria-label="Next tip"
                >
                  <ArrowRight size={16} />
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
});

DailyTip.displayName = "DailyTip";

export default DailyTip;
